import { useState, useEffect } from 'react';
import { Search, Filter, Plus, Download, Upload, Users, Mail, MessageCircle, Send, Check, Edit, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import GuestCard from '../GuestCard';
import AddGuestDialog from '../AddGuestDialog';
import EditGuestDialog from '../EditGuestDialog';
import { guestService } from '@/services/guest.service';
import { vendorService } from '@/services/vendor.service';
import { emailService } from '@/services/email.service';
import { einviteService } from '@/services/einvite.service';
import { openWhatsApp, openWhatsAppForMultiple } from '@/utils/whatsapp';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { permissions } from '@/lib/mockAuth';
import { canAddResource, isTrialExpired } from '@/utils/trialUtils';
import { BlockedActionDialog } from '@/components/BlockedActionDialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

export const GuestModule = ({ weddingId, wedding }) => {
  const { activeOrg, userRole } = useAuth();
  const canManage = permissions.canManageGuests(userRole);
  const isReadOnly = permissions.isReadOnly(userRole);
  const [guests, setGuests] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sideFilter, setSideFilter] = useState('all');
  const [rsvpFilter, setRsvpFilter] = useState('all');
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingGuest, setEditingGuest] = useState(null);
  const [selectedGuests, setSelectedGuests] = useState([]);
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const [inviteMethod, setInviteMethod] = useState('email');
  const [emailType, setEmailType] = useState('invitation'); // 'invitation' or 'accommodation'
  const [isBulkEditDialogOpen, setIsBulkEditDialogOpen] = useState(false);
  const [isBulkDeleteDialogOpen, setIsBulkDeleteDialogOpen] = useState(false);
  const [isBulkAccommodationDialogOpen, setIsBulkAccommodationDialogOpen] = useState(false);
  const [isShareEinviteDialogOpen, setIsShareEinviteDialogOpen] = useState(false);
  const [einviteShareMethod, setEinviteShareMethod] = useState('email');
  const [bulkRsvpStatus, setBulkRsvpStatus] = useState('');
  const [accommodationMethod, setAccommodationMethod] = useState('both');
  const [blockedDialogOpen, setBlockedDialogOpen] = useState(false);
  const [publishedInvite, setPublishedInvite] = useState(null);

  // Check trial limits
  const guestLimitCheck = canAddResource(wedding, 'guests', guests.length);
  const isBlocked = !guestLimitCheck.canAdd || isTrialExpired(wedding);

  // Load guests on component mount
  useEffect(() => {
    const loadData = async () => {
      if (!activeOrg?.id || !weddingId) return;
      
      try {
        setLoading(true);
        const [guestsData, vendorsData, inviteData] = await Promise.all([
          guestService.getGuests(weddingId, activeOrg.id),
          vendorService.getVendors(weddingId, activeOrg.id),
          einviteService.getPublishedInvite(weddingId, activeOrg.id).catch(() => null)
        ]);
        setGuests(guestsData);
        setVendors(vendorsData);
        setPublishedInvite(inviteData);
      } catch (error) {
        console.error('Failed to load data:', error);
        toast.error('Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [activeOrg?.id, weddingId]);

  // Get hotel vendors (Hotels category OR any vendor that provides accommodation)
  const hotelVendors = vendors.filter(v => 
    v.category === 'Hotels' || 
    v.category === 'Accommodation' || 
    v.providesAccommodation === true ||
    v.provides_accommodation === true
  );

  // Calculate stats
  const totalGuests = guests.reduce((sum, g) => {
    const count = parseInt(g.guestCount);
    return sum + (isNaN(count) ? 0 : count);
  }, 0);
  const confirmedGuests = guests.filter(g => g.rsvpStatus === 'confirmed');
  const confirmedCount = confirmedGuests.reduce((sum, g) => {
    const count = parseInt(g.guestCount);
    return sum + (isNaN(count) ? 0 : count);
  }, 0);
  const pendingCount = guests.filter(g => g.rsvpStatus === 'pending').length;
  const withAccommodation = guests.filter(g => g.hotelName).length;

  // Filter guests
  const filteredGuests = guests.filter(guest => {
    const searchLower = searchQuery.toLowerCase();
    const matchesSearch = 
      (guest.name && guest.name.toLowerCase().includes(searchLower)) ||
      (guest.email && guest.email.toLowerCase().includes(searchLower)) ||
      (guest.phone && guest.phone.includes(searchQuery));
    
    const matchesSide = sideFilter === 'all' || guest.side === sideFilter;
    const matchesRSVP = rsvpFilter === 'all' || guest.rsvpStatus === rsvpFilter;

    return matchesSearch && matchesSide && matchesRSVP;
  });

  const handleAddGuest = async (newGuest) => {
    try {
      const createdGuest = await guestService.createGuest(activeOrg.id, weddingId, newGuest);
      setGuests([...guests, createdGuest]);
      toast.success('Guest added successfully');
    } catch (error) {
      console.error('Error adding guest:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to add guest';
      toast.error(errorMessage);
      
      // If trial limit reached, show upgrade message
      if (errorMessage.includes('Trial limit') || errorMessage.includes('Upgrade')) {
        // Error message already contains upgrade guidance
      }
    }
  };

  const handleEditGuest = (guest) => {
    setEditingGuest(guest);
    setIsEditDialogOpen(true);
  };

  const handleUpdateGuest = async (updatedGuest) => {
    try {
      const updated = await guestService.updateGuest(activeOrg.id, weddingId, updatedGuest.id, updatedGuest);
      setGuests(guests.map(g => g.id === updatedGuest.id ? updated : g));
      toast.success('Guest updated successfully');
    } catch (error) {
      console.error('Error updating guest:', error);
      toast.error('Failed to update guest');
    }
  };

  const handleDeleteGuest = async (id) => {
    try {
      await guestService.deleteGuest(activeOrg.id, weddingId, id);
      setGuests(guests.filter(g => g.id !== id));
      toast.success('Guest removed from list');
    } catch (error) {
      console.error('Error deleting guest:', error);
      toast.error('Failed to delete guest');
    }
  };

  const handleSelectGuest = (guestId, checked) => {
    if (checked) {
      setSelectedGuests([...selectedGuests, guestId]);
    } else {
      setSelectedGuests(selectedGuests.filter(id => id !== guestId));
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedGuests(filteredGuests.map(g => g.id));
    } else {
      setSelectedGuests([]);
    }
  };

  const handleSendInvites = () => {
    if (selectedGuests.length === 0) {
      toast.error('Please select at least one guest');
      return;
    }
    setIsInviteDialogOpen(true);
  };

  const handleConfirmInvite = async () => {
    if (inviteMethod === 'email') {
      try {
        let result;
        
        if (emailType === 'invitation') {
          // Send wedding invitation
          result = await emailService.sendInvitations({
            guest_ids: selectedGuests,
            wedding_id: weddingId
          });
          
          if (result.status === 'success') {
            toast.success(`✅ Wedding invitations sent to ${result.sent_count} guest(s)!`, {
              description: result.failed_count > 0 ? `${result.failed_count} failed (check email addresses)` : undefined
            });
          } else {
            toast.error('Failed to send invitations. Please try again.');
          }
        } else {
          // Send accommodation details
          result = await emailService.sendAccommodation({
            guest_ids: selectedGuests,
            wedding_id: weddingId
          });
          
          if (result.status === 'success') {
            toast.success(`✅ Accommodation details sent to ${result.sent_count} guest(s)!`, {
              description: result.failed_count > 0 ? `${result.failed_count} failed (check email addresses or accommodation assignments)` : undefined
            });
          } else {
            toast.error('Failed to send accommodation details. Please try again.');
          }
        }
      } catch (error) {
        console.error('Error sending emails:', error);
        toast.error('Failed to send emails: ' + (error.response?.data?.detail || error.message));
      }
    } else {
      // Open WhatsApp for selected guests
      const selectedGuestData = guests.filter(g => selectedGuests.includes(g.id));
      const contacts = selectedGuestData.map(g => ({
        phone: g.phone,
        name: g.name
      }));

      const message = emailType === 'invitation'
        ? `🎉 You're invited to ${wedding?.bride_name || 'Bride'} & ${wedding?.groom_name || 'Groom'}'s wedding!\n\n📅 Date: ${wedding?.wedding_date || 'TBD'}\n\nWe look forward to celebrating with you!`
        : `🏨 Accommodation Details for ${wedding?.bride_name || 'Bride'} & ${wedding?.groom_name || 'Groom'}'s Wedding\n\nPlease check your email for complete details.`;

      const result = openWhatsAppForMultiple(contacts, message);
      
      if (result.opened > 0) {
        toast.success(`Opening WhatsApp for ${result.opened} guest(s)...`, {
          description: result.failed > 0 ? `${result.failed} guest(s) don't have phone numbers` : undefined
        });
      } else {
        toast.error('No guests with phone numbers selected. Please select guests with phone numbers.');
      }
    }
    
    setIsInviteDialogOpen(false);
    setSelectedGuests([]);
    setInviteMethod('email');
    setEmailType('invitation');
  };

  const handleImport = () => {
    toast.info('Excel import coming soon!');
  };

  const handleExport = () => {
    toast.success('Guest list exported to Excel');
  };

  const handleBulkEdit = () => {
    if (selectedGuests.length === 0) {
      toast.error('Please select at least one guest');
      return;
    }
    setIsBulkEditDialogOpen(true);
  };

  const handleConfirmBulkEdit = async () => {
    if (!bulkRsvpStatus) {
      toast.error('Please select an RSVP status');
      return;
    }
    
    try {
      await guestService.bulkUpdateGuests(activeOrg.id, weddingId, selectedGuests, { rsvpStatus: bulkRsvpStatus });
      
      setGuests(guests.map(g => 
        selectedGuests.includes(g.id) 
          ? { ...g, rsvpStatus: bulkRsvpStatus }
          : g
      ));
      
      toast.success(`Updated RSVP status for ${selectedGuests.length} guest(s)`);
      setIsBulkEditDialogOpen(false);
      setSelectedGuests([]);
      setBulkRsvpStatus('');
    } catch (error) {
      console.error('Error bulk updating guests:', error);
      toast.error('Failed to update guests');
    }
  };

  const handleBulkDelete = () => {
    if (selectedGuests.length === 0) {
      toast.error('Please select at least one guest');
      return;
    }
    setIsBulkDeleteDialogOpen(true);
  };

  const handleConfirmBulkDelete = async () => {
    try {
      await guestService.bulkDelete(activeOrg.id, weddingId, selectedGuests);
      setGuests(guests.filter(g => !selectedGuests.includes(g.id)));
      toast.success(`Deleted ${selectedGuests.length} guest(s)`);
      setIsBulkDeleteDialogOpen(false);
      setSelectedGuests([]);
    } catch (error) {
      console.error('Error bulk deleting guests:', error);
      toast.error('Failed to delete guests');
    }
  };

  const handleBulkSendAccommodation = () => {
    if (selectedGuests.length === 0) {
      toast.error('Please select at least one guest');
      return;
    }
    setIsBulkAccommodationDialogOpen(true);
  };

  const handleConfirmSendAccommodation = async () => {
    try {
      const selectedGuestData = guests.filter(g => selectedGuests.includes(g.id));
      
      if (accommodationMethod === 'whatsapp' || accommodationMethod === 'both') {
        // Open WhatsApp for guests with phone numbers
        const contactsWithPhone = selectedGuestData
          .filter(g => g.phone)
          .map(g => ({
            phone: g.phone,
            name: g.name,
            hotelName: g.hotelName,
            roomNumber: g.roomNumber,
            checkInDate: g.checkInDate,
            checkOutDate: g.checkOutDate
          }));

        if (contactsWithPhone.length > 0) {
          const message = `🏨 Accommodation Details for ${wedding?.bride_name || 'Bride'} & ${wedding?.groom_name || 'Groom'}'s Wedding\n\n` +
            contactsWithPhone.map(c => {
              let msg = `Dear ${c.name},\n\n`;
              if (c.hotelName) msg += `Hotel: ${c.hotelName}\n`;
              if (c.roomNumber) msg += `Room: ${c.roomNumber}\n`;
              if (c.checkInDate) msg += `Check-in: ${c.checkInDate}\n`;
              if (c.checkOutDate) msg += `Check-out: ${c.checkOutDate}\n`;
              return msg;
            }).join('\n---\n\n') +
            `\nWe look forward to seeing you!`;

          // For "both", we'll open WhatsApp for first guest, then send emails
          if (accommodationMethod === 'whatsapp' || contactsWithPhone.length === 1) {
            const result = openWhatsAppForMultiple(contactsWithPhone, message);
            if (result.opened > 0) {
              toast.success(`Opening WhatsApp for ${result.opened} guest(s)...`);
            }
          } else {
            // For multiple guests with "both", open WhatsApp for first one
            openWhatsApp(contactsWithPhone[0].phone, message);
            toast.success(`Opening WhatsApp for ${contactsWithPhone[0].name}...`);
          }
        } else {
          toast.warning('No guests with phone numbers selected for WhatsApp.');
        }
      }

      if (accommodationMethod === 'email' || accommodationMethod === 'both') {
        // Send emails
        const result = await emailService.sendAccommodation({
          guest_ids: selectedGuests,
          wedding_id: weddingId
        });
        
        if (result.sent_count > 0) {
          toast.success(
            `Accommodation details sent to ${result.sent_count} guest(s) via Email!`,
            {
              description: result.failed_count > 0 
                ? `${result.failed_count} email(s) failed. Check guest email addresses.`
                : 'All emails sent successfully'
            }
          );
        } else {
          toast.error('Failed to send emails. Please check guest details and try again.');
        }
      }
      
      setIsBulkAccommodationDialogOpen(false);
      setSelectedGuests([]);
      setAccommodationMethod('both');
    } catch (error) {
      console.error('Error sending accommodation details:', error);
      toast.error('Failed to send accommodation details. Please try again.');
    }
  };

  const handleShareEinvite = () => {
    if (selectedGuests.length === 0) {
      toast.error('Please select at least one guest');
      return;
    }
    
    if (!publishedInvite) {
      toast.error('No published E-invite found. Please publish an E-invite first from the E-invite module.');
      return;
    }
    
    setIsShareEinviteDialogOpen(true);
  };

  const handleConfirmShareEinvite = async () => {
    if (!publishedInvite) {
      toast.error('No published E-invite found. Please publish an E-invite first.');
      return;
    }

    try {
      if (einviteShareMethod === 'email') {
        const result = await einviteService.shareInvite(
          weddingId,
          activeOrg.id,
          selectedGuests,
          'email',
          publishedInvite.id
        );
        
        if (result.sent_count > 0) {
          toast.success(
            `E-invite shared with ${result.sent_count} guest(s) via Email!`,
            {
              description: result.failed_count > 0 
                ? `${result.failed_count} email(s) failed. Check guest email addresses.`
                : 'All emails sent successfully'
            }
          );
        } else {
          toast.error('Failed to share E-invite. Please check guest email addresses and try again.');
        }
      } else {
        // Open WhatsApp for selected guests
        const selectedGuestData = guests.filter(g => selectedGuests.includes(g.id));
        const contacts = selectedGuestData
          .filter(g => g.phone)
          .map(g => ({
            phone: g.phone,
            name: g.name
          }));

        if (contacts.length === 0) {
          toast.error('No guests with phone numbers selected. Please select guests with phone numbers.');
          return;
        }

        const message = `🎉 You're invited to ${wedding?.bride_name || 'Bride'} & ${wedding?.groom_name || 'Groom'}'s wedding!\n\n` +
          `View your digital invitation: ${publishedInvite.share_url}`;

        const result = openWhatsAppForMultiple(contacts, message);
        
        if (result.opened > 0) {
          toast.success(`Opening WhatsApp for ${result.opened} guest(s)...`, {
            description: result.failed > 0 ? `${result.failed} guest(s) don't have phone numbers` : undefined
          });
        } else {
          toast.error('Failed to open WhatsApp. Please check guest phone numbers.');
        }
      }
      
      setIsShareEinviteDialogOpen(false);
      setSelectedGuests([]);
      setEinviteShareMethod('email');
    } catch (error) {
      console.error('Error sharing E-invite:', error);
      toast.error('Failed to share E-invite: ' + (error.response?.data?.detail || error.message));
    }
  };

  return (
    <div>
      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card className="p-4 border-t-4 border-t-primary">
          <p className="text-sm text-muted-foreground mb-1">Total Guests</p>
          <p className="text-2xl font-bold text-foreground">{totalGuests}</p>
        </Card>
        <Card className="p-4 border-t-4 border-t-success">
          <p className="text-sm text-muted-foreground mb-1">Confirmed</p>
          <p className="text-2xl font-bold text-success">{confirmedCount}</p>
        </Card>
        <Card className="p-4 border-t-4 border-t-warning">
          <p className="text-sm text-muted-foreground mb-1">Pending</p>
          <p className="text-2xl font-bold text-warning">{pendingCount}</p>
        </Card>
        <Card className="p-4 border-t-4 border-t-accent">
          <p className="text-sm text-muted-foreground mb-1">With Accommodation</p>
          <p className="text-2xl font-bold text-accent">{withAccommodation}</p>
        </Card>
      </div>

      {/* Action Bar */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search guests..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={sideFilter} onValueChange={setSideFilter}>
          <SelectTrigger className="w-full sm:w-[180px]">
            <SelectValue placeholder="Filter by side" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sides</SelectItem>
            <SelectItem value="Bride Side">Bride Side</SelectItem>
            <SelectItem value="Groom Side">Groom Side</SelectItem>
            <SelectItem value="Common">Common</SelectItem>
          </SelectContent>
        </Select>
        <Select value={rsvpFilter} onValueChange={setRsvpFilter}>
          <SelectTrigger className="w-full sm:w-[180px]">
            <SelectValue placeholder="Filter by RSVP" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="confirmed">Confirmed</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="declined">Declined</SelectItem>
          </SelectContent>
        </Select>
        {canManage && (
          <>
            <Button variant="outline" onClick={handleImport}>
              <Upload className="h-4 w-4 mr-2" />
              Import
            </Button>
            <Button variant="outline" onClick={handleExport}>
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
          </>
        )}
        {(canManage || !isReadOnly) && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button 
                    onClick={() => setIsAddDialogOpen(true)} 
                    className="gradient-primary" 
                    disabled={isReadOnly || isBlocked}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Guest
                  </Button>
                </span>
              </TooltipTrigger>
              {isBlocked && (
                <TooltipContent>
                  <p>🔒 Trial limit reached<br />Upgrade for more guests</p>
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        )}
      </div>

      {/* Bulk Actions */}
      {selectedGuests.length > 0 && (
        <Card className="p-4 mb-4 bg-primary/5 border-primary/20">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <Checkbox
                checked={selectedGuests.length === filteredGuests.length}
                onCheckedChange={handleSelectAll}
              />
              <span className="text-sm font-medium">
                {selectedGuests.length} guest(s) selected
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {(canManage || !isReadOnly) && (
                <Button onClick={handleBulkEdit} variant="outline" size="sm" disabled={isReadOnly}>
                  <Edit className="h-4 w-4 mr-2" />
                  Bulk Edit RSVP
                </Button>
              )}
              {canManage && (
                <>
                  {publishedInvite && (
                    <Button onClick={handleShareEinvite} variant="outline" size="sm">
                      <MessageCircle className="h-4 w-4 mr-2" />
                      Share E-invite
                    </Button>
                  )}
                  <Button onClick={handleBulkSendAccommodation} variant="outline" size="sm">
                    <Mail className="h-4 w-4 mr-2" />
                    Send Accommodation
                  </Button>
                  <Button onClick={handleBulkDelete} variant="outline" size="sm" className="text-destructive hover:bg-destructive hover:text-destructive-foreground">
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Selected
                  </Button>
                  <Button onClick={handleSendInvites} className="gradient-primary" size="sm">
                    <Send className="h-4 w-4 mr-2" />
                    Send Invites
                  </Button>
                </>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* Guest List */}
      {loading ? (
        <Card className="p-12 text-center">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading guests...</p>
        </Card>
      ) : filteredGuests.length === 0 ? (
        <Card className="p-12 text-center">
          <Users className="h-16 w-16 mx-auto text-muted-foreground/50 mb-4" />
          <h3 className="text-xl font-semibold mb-2">No guests found</h3>
          <p className="text-muted-foreground mb-4">
            {guests.length === 0 ? 'Start by adding your first guest' : 'Try adjusting your search or filters'}
          </p>
          {(canManage || !isReadOnly) && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Button 
                      onClick={() => setIsAddDialogOpen(true)} 
                      className="gradient-primary" 
                      disabled={isReadOnly || isBlocked}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Guest
                    </Button>
                  </span>
                </TooltipTrigger>
                {isBlocked && (
                  <TooltipContent>
                    <p>🔒 Trial limit reached<br />Upgrade for more guests</p>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          )}
        </Card>
      ) : (
        <div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
          onClick={(e) => {
            if (!selectedGuests.length) return;
            if (!e.target.closest('[data-guest-card]')) {
              setSelectedGuests([]);
            }
          }}
        >
          {filteredGuests.map((guest) => {
            const isSelected = selectedGuests.includes(guest.id);
            return (
              <div 
                key={guest.id} 
                data-guest-card
                className={`relative transition-all duration-200 rounded-lg ${
                  isSelected 
                    ? 'ring-4 ring-primary ring-offset-2 shadow-lg' 
                    : 'ring-0'
                }`}
              >
                {/* Checkbox positioned at top-right corner */}
                <div 
                  className="absolute -top-2 -right-2 z-50 bg-white rounded-md shadow-md p-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelectGuest(guest.id, !isSelected);
                  }}
                >
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={(checked) => handleSelectGuest(guest.id, checked)}
                    className="h-5 w-5 border-2 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                  />
                </div>
                
                {/* Card wrapper - clickable for selection */}
                <div 
                  className="cursor-pointer"
                  onClick={(e) => {
                    // Don't select if clicking on buttons or interactive elements
                    if (!e.target.closest('button') && !e.target.closest('a') && !e.target.closest('[role="checkbox"]')) {
                      handleSelectGuest(guest.id, !isSelected);
                    }
                  }}
                >
                  <GuestCard
                    guest={guest}
                    onUpdate={handleUpdateGuest}
                    onEdit={() => handleEditGuest(guest)}
                    onDelete={handleDeleteGuest}
                    onSendAccommodation={async (guestId) => {
                      // Mock implementation - will be replaced with actual API call
                      return Promise.resolve();
                    }}
                    hotelVendors={hotelVendors}
                    existingGuests={guests}
                    canEdit={canManage || !isReadOnly}
                    canDelete={canManage}
                    isReadOnly={isReadOnly}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add Guest Dialog */}
      <AddGuestDialog
        open={isAddDialogOpen}
        onOpenChange={setIsAddDialogOpen}
        onAddGuest={handleAddGuest}
        hotelVendors={hotelVendors}
        existingGuests={guests}
      />

      {/* Edit Guest Dialog */}
      <EditGuestDialog
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        guest={editingGuest}
        onUpdateGuest={handleUpdateGuest}
        hotelVendors={hotelVendors}
        existingGuests={guests}
      />

      {/* Send Invite Dialog */}
      <Dialog open={isInviteDialogOpen} onOpenChange={setIsInviteDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Send Email to Guests</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              Send emails to {selectedGuests.length} selected guest(s)
            </p>
            
            {/* Email Type Selection */}
            <div>
              <Label className="mb-3 block">What would you like to send?</Label>
              <RadioGroup value={emailType} onValueChange={setEmailType}>
                <div className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-muted/50 cursor-pointer">
                  <RadioGroupItem value="invitation" id="invitation" />
                  <Label htmlFor="invitation" className="flex items-center gap-2 cursor-pointer flex-1">
                    <Mail className="h-4 w-4 text-primary" />
                    <div>
                      <div className="font-medium">Wedding Invitation</div>
                      <div className="text-xs text-muted-foreground">Send formal wedding invitation with event details</div>
                    </div>
                  </Label>
                </div>
                <div className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-muted/50 cursor-pointer">
                  <RadioGroupItem value="accommodation" id="accommodation" />
                  <Label htmlFor="accommodation" className="flex items-center gap-2 cursor-pointer flex-1">
                    <Mail className="h-4 w-4 text-primary" />
                    <div>
                      <div className="font-medium">Accommodation Details</div>
                      <div className="text-xs text-muted-foreground">Send hotel booking and check-in information</div>
                    </div>
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {/* Delivery Method */}
            <div>
              <Label className="mb-3 block">Delivery method:</Label>
              <RadioGroup value={inviteMethod} onValueChange={setInviteMethod}>
                <div className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-muted/50 cursor-pointer">
                  <RadioGroupItem value="email" id="method-email" />
                  <Label htmlFor="method-email" className="flex items-center gap-2 cursor-pointer flex-1">
                    <Mail className="h-4 w-4 text-primary" />
                    <div>
                      <div className="font-medium">Email</div>
                      <div className="text-xs text-muted-foreground">Send via email</div>
                    </div>
                  </Label>
                </div>
                <div className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-muted/50 cursor-pointer opacity-50">
                  <RadioGroupItem value="whatsapp" id="method-whatsapp" disabled />
                  <Label htmlFor="method-whatsapp" className="flex items-center gap-2 cursor-pointer flex-1">
                    <MessageCircle className="h-4 w-4 text-success" />
                    <div>
                      <div className="font-medium">WhatsApp</div>
                      <div className="text-xs text-muted-foreground">Coming soon</div>
                    </div>
                  </Label>
                </div>
              </RadioGroup>
            </div>
            
            <div className={`p-3 rounded-lg text-xs ${emailType === 'accommodation' ? 'bg-blue-50 text-blue-900' : 'bg-purple-50 text-purple-900'}`}>
              <strong>Note:</strong> {emailType === 'accommodation' 
                ? 'Only guests with assigned accommodation and valid email addresses will receive the email.'
                : 'All selected guests with valid email addresses will receive the wedding invitation.'}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsInviteDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleConfirmInvite} className="gradient-primary">
              <Send className="h-4 w-4 mr-2" />
              Send {emailType === 'invitation' ? 'Invitations' : 'Details'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Edit RSVP Dialog */}
      <Dialog open={isBulkEditDialogOpen} onOpenChange={setIsBulkEditDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Bulk Update RSVP Status</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              Update RSVP status for {selectedGuests.length} selected guest(s)
            </p>
            <div className="space-y-2">
              <Label htmlFor="rsvp-status">Select RSVP Status</Label>
              <Select value={bulkRsvpStatus} onValueChange={setBulkRsvpStatus}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="declined">Declined</SelectItem>
                  <SelectItem value="maybe">Maybe</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsBulkEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleConfirmBulkEdit} className="gradient-primary">
              <Check className="h-4 w-4 mr-2" />
              Update Status
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Dialog */}
      <AlertDialog open={isBulkDeleteDialogOpen} onOpenChange={setIsBulkDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedGuests.length} Guest(s)?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the selected guests from your wedding list.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmBulkDelete} className="bg-destructive hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Share E-invite Dialog */}
      <Dialog open={isShareEinviteDialogOpen} onOpenChange={setIsShareEinviteDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Share E-invite</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              Share the published E-invite with {selectedGuests.length} selected guest(s)
            </p>
            <div>
              <Label className="mb-3 block">Choose delivery method:</Label>
              <RadioGroup value={einviteShareMethod} onValueChange={setEinviteShareMethod}>
                <div className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-muted/50 cursor-pointer">
                  <RadioGroupItem value="email" id="einvite-email" />
                  <Label htmlFor="einvite-email" className="flex items-center gap-2 cursor-pointer flex-1">
                    <Mail className="h-4 w-4 text-primary" />
                    <div>
                      <div className="font-medium">Email</div>
                      <div className="text-xs text-muted-foreground">Send E-invite link via email</div>
                    </div>
                  </Label>
                </div>
                <div className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-muted/50 cursor-pointer">
                  <RadioGroupItem value="whatsapp" id="einvite-whatsapp" />
                  <Label htmlFor="einvite-whatsapp" className="flex items-center gap-2 cursor-pointer flex-1">
                    <MessageCircle className="h-4 w-4 text-success" />
                    <div>
                      <div className="font-medium">WhatsApp</div>
                      <div className="text-xs text-muted-foreground">Send E-invite link via WhatsApp</div>
                    </div>
                  </Label>
                </div>
              </RadioGroup>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsShareEinviteDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleConfirmShareEinvite} className="gradient-primary">
              <Send className="h-4 w-4 mr-2" />
              Share
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Send Accommodation Dialog */}
      <Dialog open={isBulkAccommodationDialogOpen} onOpenChange={setIsBulkAccommodationDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Send Accommodation Details</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              Send accommodation details to {selectedGuests.length} selected guest(s)
            </p>
            <div>
              <Label className="mb-3 block">Choose delivery method:</Label>
              <RadioGroup value={accommodationMethod} onValueChange={setAccommodationMethod}>
                <div className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-muted/50 cursor-pointer">
                  <RadioGroupItem value="both" id="both" />
                  <Label htmlFor="both" className="flex items-center gap-2 cursor-pointer flex-1">
                    <div className="flex gap-2">
                      <Mail className="h-4 w-4 text-primary" />
                      <MessageCircle className="h-4 w-4 text-success" />
                    </div>
                    <div>
                      <div className="font-medium">Email & WhatsApp</div>
                      <div className="text-xs text-muted-foreground">Send via both channels</div>
                    </div>
                  </Label>
                </div>
                <div className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-muted/50 cursor-pointer">
                  <RadioGroupItem value="email" id="acc-email" />
                  <Label htmlFor="acc-email" className="flex items-center gap-2 cursor-pointer flex-1">
                    <Mail className="h-4 w-4 text-primary" />
                    <div>
                      <div className="font-medium">Email Only</div>
                      <div className="text-xs text-muted-foreground">Send via email</div>
                    </div>
                  </Label>
                </div>
                <div className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-muted/50 cursor-pointer">
                  <RadioGroupItem value="whatsapp" id="acc-whatsapp" />
                  <Label htmlFor="acc-whatsapp" className="flex items-center gap-2 cursor-pointer flex-1">
                    <MessageCircle className="h-4 w-4 text-success" />
                    <div>
                      <div className="font-medium">WhatsApp Only</div>
                      <div className="text-xs text-muted-foreground">Send via WhatsApp</div>
                    </div>
                  </Label>
                </div>
              </RadioGroup>
            </div>
            <div className="bg-muted/50 p-3 rounded-lg text-xs text-muted-foreground">
              <strong>Note:</strong> Accommodation details will include hotel name, address, check-in/check-out dates, and booking reference (if available).
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsBulkAccommodationDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleConfirmSendAccommodation} className="gradient-primary">
              <Send className="h-4 w-4 mr-2" />
              Send Details
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BlockedActionDialog
        open={blockedDialogOpen}
        onOpenChange={setBlockedDialogOpen}
        resourceType="guests"
        limit={guestLimitCheck.limit}
        wedding={wedding}
        onUpgradeSuccess={async () => {
          const [guestsData, vendorsData] = await Promise.all([
            guestService.getGuests(weddingId, activeOrg.id),
            vendorService.getVendors(weddingId, activeOrg.id)
          ]);
          setGuests(guestsData);
          setVendors(vendorsData);
        }}
      />
    </div>
  );
};

export default GuestModule;
